import type { DeviceType } from '@/types/planAssistant';

export interface PlanSlide {
  id: string;
  slug: string;
  title: string;
  headline: string;
  bullets: string[];
  priceText: string;
  badge: string;
  deviceType: DeviceType;
  image?: string;
  persona?: {
    title: string;
    description: string;
    icon: string;
  };
  detailsAccordion?: {
    hotspot: string;
    speedReduction: string;
    cancellation: string;
  };
}

export const planSlides: PlanSlide[] = [
  {
    id: 'starter-1gb',
    slug: 'perch-starter-1gb',
    title: 'Perch Starter',
    headline: '1 GB',
    bullets: ['1GB of 5G data', 'Unlimited talk and text'],
    priceText: '$14.99 per month',
    badge: '',
    deviceType: 'phone',
  },
  {
    id: 'starter-intl-1gb',
    slug: 'perch-starter-international-1gb',
    title: 'Perch Starter International',
    headline: '1 GB',
    bullets: [
      '1GB of 5G data',
      'Unlimited talk and text',
      'Includes the US and 85+ countries',
    ],
    priceText: '$12.50 per month',
    badge: 'International',
    deviceType: 'phone',
  },
  {
    id: 'plus-3gb',
    slug: 'perch-plus-3gb',
    title: 'Perch Plus 3',
    headline: '3 GB',
    bullets: ['3GB of 5G data', 'Unlimited talk and text'],
    priceText: '$14.99 per month',
    badge: '',
    deviceType: 'phone',
  },
  {
    id: 'plus-5gb',
    slug: 'perch-plus-5gb',
    title: 'Perch Plus 5',
    headline: '5 GB',
    bullets: ['5GB of 5G data', 'Unlimited talk and text'],
    priceText: '$19.99 per month',
    badge: '',
    deviceType: 'phone',
  },
  {
    id: 'plus-5gb-intl',
    slug: 'perch-plus-5gb-international',
    title: 'Perch Plus 5 International',
    headline: '5 GB',
    bullets: [
      '5GB of 5G data',
      'Unlimited talk and text',
      'Includes the US and 85+ countries',
    ],
    priceText: '$24.99 per month',
    badge: 'International',
    deviceType: 'phone',
  },
  {
    id: 'plus-10gb',
    slug: 'perch-plus-10gb',
    title: 'Perch Plus 10',
    headline: '10 GB',
    bullets: ['10GB of 5G data', 'Unlimited talk and text'],
    priceText: '$36.50 per month',
    badge: '',
    deviceType: 'phone',
  },
  {
    id: 'plus-10gb-intl',
    slug: 'perch-plus-10gb-international',
    title: 'Perch Plus 10 International',
    headline: '10 GB',
    bullets: [
      '10GB of 5G data',
      'Unlimited talk and text',
      'Includes the US and 85+ countries',
    ],
    priceText: '$38.50 per month',
    badge: 'International',
    deviceType: 'phone',
  },
  {
    id: 'plus-20gb',
    slug: 'perch-plus-20gb',
    title: 'Perch Plus 20',
    headline: '20 GB',
    bullets: ['20GB of 5G data', 'Unlimited talk and text'],
    priceText: '$44.50 per month',
    badge: '',
    deviceType: 'phone',
  },
  {
    id: 'unlimited',
    slug: 'perch-unlimited',
    title: 'Perch Unlimited',
    headline: '32 GB',
    bullets: [
      'Never run out of data',
      '32GB of 5G data — after that your speed will reduce',
      'Unlimited talk and text',
    ],
    priceText: '$62.50 per month',
    badge: '',
    deviceType: 'phone',
  },
  {
    id: 'unlimited-intl',
    slug: 'perch-unlimited-international',
    title: 'Perch Unlimited International',
    headline: '32 GB',
    bullets: [
      'Never run out of data',
      '32 GB of 5G data — after that your speed will reduce',
      'Unlimited calls and texts to the US and 85+ countries',
    ],
    priceText: '$65.00 per month',
    badge: 'International',
    deviceType: 'phone',
  },
  {
    id: 'wearable',
    slug: 'perch-wearable',
    title: 'Perch Wearable',
    headline: '250 MB',
    bullets: ['For your tablet or Smartwatch', '250MB of 5G data'],
    priceText: '$9.99 per month',
    badge: '',
    deviceType: 'wearable',
  },
];

// New plans data for the /plans page
export const newPlans: PlanSlide[] = [
  {
    id: '2485',
    slug: 'wifi-always',
    title: 'Smart One',
    headline: '1 GB',
    bullets: [
      'Best for light data / WiFi-first use',
      '1GB of 5G data',
      'Add more data as needed',
      'Unlimited talk & text (US, Canada & Mexico)',
    ],
    priceText: '$9.99/mo',
    badge: '',
    deviceType: 'phone',
    persona: {
      title: 'The Home Office Hero',
      description: 'Mostly on Wi-Fi — low data, big savings.',
      icon: 'home',
    },
    detailsAccordion: {
      hotspot: 'Mobile hotspot not included on this plan.',
      speedReduction: 'After 1GB, speeds reduced to 2G (128kbps).',
      cancellation: 'No contracts. Cancel anytime from your account dashboard.',
    },
  },
  {
    id: '2488',
    slug: 'unlimited-5',
    title: 'Smart Five',
    headline: '5 GB',
    bullets: [
      'Great for everyday use',
      '5GB of 5G data to start',
      'Add more data as needed',
      'Unlimited talk & text (US, Canada & Mexico)',
    ],
    priceText: '$14.99/mo',
    badge: '',
    deviceType: 'phone',
    persona: {
      title: 'The Coffee Shop Regular',
      description: 'A little Wi-Fi, a little data — just right.',
      icon: 'coffee',
    },
    detailsAccordion: {
      hotspot: 'Mobile hotspot included with full-speed 5G data.',
      speedReduction: 'After 5GB, speeds reduced to 2G (128kbps).',
      cancellation: 'No contracts. Cancel anytime from your account dashboard.',
    },
  },
  {
    id: '2055',
    slug: 'wifi-sometimes',
    title: 'Wi-Fi Sometimes',
    headline: '12 GB',
    bullets: [
      'Out and about, streaming stories, staying connected on the go',
      '12GB of 5G data',
      'Unlimited talk and text to US, Canada, & Mexico',
    ],
    priceText: '$33.99/mo',
    badge: 'Popular',
    deviceType: 'phone',
    persona: {
      title: 'The Social Butterfly',
      description: 'Always on the move.',
      icon: 'signal',
    },
    detailsAccordion: {
      hotspot: 'Mobile hotspot included with full-speed 5G data.',
      speedReduction: 'After 12GB, speeds reduced to 2G (128kbps).',
      cancellation: 'No contracts. Cancel anytime from your account dashboard.',
    },
  },
  {
    id: '2493',
    slug: 'premium-unlimited',
    title: 'Premium Unlimited',
    headline: '100 GB',
    bullets: [
      'Best for heavy data users',
      'Unlimited high-speed data',
      'No need to add more — ever',
      'Unlimited talk & text (US, Canada & Mexico)',
    ],
    priceText: '$49.99/mo',
    badge: '',
    deviceType: 'phone',
    persona: {
      title: 'The Always Connected Type',
      description: 'All the data. Everywhere.',
      icon: 'infinity',
    },
    detailsAccordion: {
      hotspot: 'Mobile hotspot included with full-speed 5G data.',
      speedReduction:
        'After 22GB of high-speed data usage, speeds may be reduced to 4G during network congestion.',
      cancellation: 'No contracts. Cancel anytime from your account dashboard.',
    },
  },
  {
    id: '1963',
    slug: 'wearable-addon',
    title: 'Wearable Add-On',
    headline: '250 MB',
    bullets: ['For your tablet or Smartwatch', '250MB of 5G data'],
    priceText: '$9.99/mo',
    badge: '',
    deviceType: 'wearable',
    persona: {
      title: 'The Smartwatch Companion',
      description: 'Stay synced from your wrist.',
      icon: 'watch',
    },
    detailsAccordion: {
      hotspot: 'Hotspot not available for wearable devices.',
      speedReduction: 'After 250MB, speeds reduced to 2G (128kbps).',
      cancellation:
        'No contracts. Cancel anytime. Requires an active Perch mobile plan.',
    },
  },
];
